/**
 * The display currency a signed-out visitor CHOSE, remembered in this browser.
 *
 * WHY IT EXISTS. A guest's pick (the storefront footer select, the marketplace
 * and search "Display" popovers) used to live only in React state, so a reload
 * lost it and so did every product opened in a new tab, which is exactly how a
 * storefront visitor browses. A signed-in viewer's choice lives on the account
 * (users/{uid}.settings.currency) and is never written here; setCurrency in
 * src/context/theme-context.tsx says why.
 *
 * ONLY AN EXPLICIT CHOICE IS STORED. The detected default (the `gc` geo cookie,
 * then the timezone guess in src/lib/detect-currency.ts) is recomputed on every
 * load and never written here. Storing it would freeze a visitor on whatever
 * was detected the first time: a traveller, or a first visit that ran before
 * the geo cookie existed, would never be re-detected, with no choice ever made.
 *
 * THE KEY IS DISCLOSED. `currency` is listed in the Privacy Policy's storage
 * table (COOKIE_ROWS in src/app/privacy-policy/page.tsx) as "your chosen display
 * currency", and guest-currency.test.ts fails if that table stops naming it, so
 * rename both or neither. Builds up to 2026-01-15 wrote the same key on every
 * pick, signed in or not; a value one of them left behind is an explicit pick
 * too, so it is honoured as long as it still validates.
 *
 * THE STORED VALUE IS UNTRUSTED. The visitor, an extension or an old build can
 * write anything to localStorage, and the value becomes the ISO code handed to
 * Intl.NumberFormat (which throws a RangeError on a malformed one) and the
 * currency the checkout asks to be charged in. So it counts only when it is one
 * of the codes the pickers offer (`currencies` in ./currency-data), spelled
 * exactly as they spell it; anything else reads as "no choice", which means
 * detection. Every storage access is wrapped, because private modes, blocked
 * site data and sandboxed frames throw on it.
 *
 * Pure apart from the storage handed in, so the node tests can drive it with a
 * plain object. Keep it free of React and of anything that needs a DOM.
 */

import { currencies } from './currency-data';

/** localStorage key for a guest's chosen display currency. Disclosed by name in the Privacy Policy. */
export const GUEST_CURRENCY_STORAGE_KEY = 'currency';

/** The slice of the Web Storage API used here. `window.localStorage` satisfies it, and so does a test double. */
export interface GuestCurrencyStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const OFFERED_CODES: ReadonlySet<string> = new Set(currencies.map((c) => c.code));

/** True only for a code the currency pickers offer, spelled exactly as they spell it ("EUR", never "eur"). */
export function isOfferedCurrency(code: unknown): code is string {
  return typeof code === 'string' && OFFERED_CODES.has(code);
}

/**
 * This browser's localStorage, or null where there is none to use: on the
 * server (no window), and where reading the property itself throws, which is
 * what blocked site data and some private modes do (a SecurityError on
 * `window.localStorage`, before any getItem).
 */
export function browserGuestCurrencyStorage(): GuestCurrencyStorage | null {
  try {
    return typeof window === 'undefined' ? null : (window.localStorage ?? null);
  } catch {
    return null;
  }
}

/** The stored choice, or null when there is none worth using: never chose, storage unavailable, or a code we do not offer. */
export function readGuestCurrency(storage: GuestCurrencyStorage | null | undefined): string | null {
  if (!storage) return null;
  try {
    const stored = storage.getItem(GUEST_CURRENCY_STORAGE_KEY);
    return isOfferedCurrency(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Remember a guest's explicit pick. Returns whether it was stored.
 *
 * A code we do not offer is refused rather than written, so the reader never
 * meets a value it would have to throw away. Best-effort otherwise: when the
 * write fails (storage full or blocked) the pick still applies for this page,
 * which is all it ever did before it was stored.
 */
export function writeGuestCurrency(storage: GuestCurrencyStorage | null | undefined, code: string): boolean {
  if (!storage || !isOfferedCurrency(code)) return false;
  try {
    storage.setItem(GUEST_CURRENCY_STORAGE_KEY, code);
    return true;
  } catch {
    return false;
  }
}

/** The guest currency a page load starts from: the stored choice, else detection. `detect` runs only when there is no usable choice. */
export function initialGuestCurrency(
  storage: GuestCurrencyStorage | null | undefined,
  detect: () => string,
): string {
  return readGuestCurrency(storage) ?? detect();
}

/**
 * The guest currency this tab should switch to after a `storage` event, or
 * undefined when the event is about some other key and changes nothing.
 *
 * The browser fires `storage` only in the OTHER tabs of the origin, never in
 * the one that wrote, so this is how a pick made in one tab reaches the others.
 * The new value is validated exactly like the initial read, and a removed,
 * cleared (`key === null` is localStorage.clear()) or unusable value falls back
 * to detection, so every open tab ends up showing what a reload would.
 */
export function guestCurrencyAfterStorageEvent(
  event: { key: string | null; newValue: string | null },
  detect: () => string,
): string | undefined {
  if (event.key !== null && event.key !== GUEST_CURRENCY_STORAGE_KEY) return undefined;
  const choice = event.key === null ? null : event.newValue;
  return isOfferedCurrency(choice) ? choice : detect();
}
